import { WahlFeld } from "../design/WahlFeld";
import { useEffect, useId, useRef, useState, type ReactNode, type SelectHTMLAttributes } from "react";
import { useNavigate } from "react-router";
import { useAkten, useFristAendern, useFristAnlegen, useFristen, useFristLoeschen, usePersonen } from "../api/abfragen";
import { ApiFehler, fehlerText } from "../api/client";
import type { Dokument, Frist, Fristart, Vorschlag } from "../api/typen";
import { Feld } from "../design/Feld";
import { Knopf } from "../design/Knopf";
import { Fehlertext } from "../design/Leer";
import { Rueckfrage } from "../design/Rueckfrage";
import { Sheet } from "../design/Sheet";
import { useSichern } from "../design/sichern";
import { useToast } from "../design/toastKontext";
import { betragEingabe, datum as datumText, FRISTARTEN, hoechstens } from "../format";
import { heuteBerlin } from "../welt/lage";
import { FRIST_NEU_TEXTE, FRIST_TEXTE, fristAenderung, fristGrenzen, LEER, TITEL_MAX, werteVon, type Werte } from "./fristNeu";
import { FRISTWORT } from "../welt/saetze";
import { abgeleiteteFrist, abgeleitetBasis } from "./vorschlag";

type Fehler = { titel?: string; datum?: string; betrag?: string; gesamt?: unknown };

/** 422 des Servers am Feld: Datum außerhalb der Grenzen (Text des Dienstes oder `loc` datum), Titel oder Bezug, Betrag; alles
 *  andere über dem Blatt. */
function serverFehler(fehler: unknown, datumSatz: string): Fehler {
  // nie der Text des Browsers („Failed to fetch“): ohne Netz der Satz der App (Regel V75w R1 A2, wie Posteingang.tsx)
  if (!(fehler instanceof ApiFehler)) return { gesamt: new Error(fehlerText(fehler)) };
  if (fehler.status !== 422) return { gesamt: fehler };
  const detail = (fehler.daten as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return /datum/i.test(detail) ? { datum: datumSatz } : { gesamt: fehler };
  if (!Array.isArray(detail)) return { gesamt: fehler };
  const f: Fehler = {};
  for (const e of detail as { loc?: unknown }[]) {
    const ort = Array.isArray(e.loc) ? e.loc.map(String) : [];
    if (ort.includes("datum")) f.datum = datumSatz;
    else if (ort.includes("betrag")) f.betrag = FRIST_NEU_TEXTE.betrag;
    // W82 Web-5h1: die Person steht über dem Feld „Für wen“ nur als Satz des Blatts (Z01 kommt als Zeichenkette, siehe oben)
    else if (ort.includes("person_id")) f.gesamt = fehler;
    else if (ort.includes("titel") || ort.includes("akte_id") || ort.length <= 1) f.titel = FRIST_NEU_TEXTE.ohneBezug;
    else f.gesamt = fehler;
  }
  return f;
}

/** Auswahl in einer Gruppe (Beschriftung darüber wie `Feld`); der Hinweis ist ihre Beschreibung. */
function Auswahl(props: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hinweis?: ReactNode; children: ReactNode }) {
  const { label, hinweis, children, ...rest } = props;
  const id = useId();
  const hinweisId = useId();
  return (
    <li className="zeile zeile-feld relative flex flex-col justify-center gap-2 px-4 pt-1 pb-2">
      <label htmlFor={id} className="t-caption-1 t-caption-1-medium text-pg-text-2">
        {label}
      </label>
      <WahlFeld
        label={label}
        id={id}
        className="t-body min-h-(--feld-h) w-full rounded-md bg-pg-surface px-3 text-pg-text shadow-(--pg-feld-rand) outline-none transition-shadow duration-(--pg-dur-fast) focus-visible:shadow-(--pg-feld-fokus)"
        aria-describedby={hinweis ? hinweisId : undefined}
        {...rest}
      >
        {children}
      </WahlFeld>
      {hinweis && (
        <span id={hinweisId} className="t-footnote text-pg-text-2">
          {hinweis}
        </span>
      )}
    </li>
  );
}

/** Ziel des Fokus nach dem Löschen: die nachrückende Zeile der Gruppe (danach, sonst davor), gemerkt vor dem Löschen. */
function nachrueckend(id: string): string[] {
  const zeile = document.querySelector(`[data-frist-zeile="${CSS.escape(id)}"]`);
  return [zeile?.nextElementSibling, zeile?.previousElementSibling].flatMap((z) => {
    const nachbar = z?.getAttribute("data-frist-zeile");
    return nachbar ? [nachbar] : [];
  });
}
function zeileZiel(ids: string[]): HTMLElement | null {
  for (const id of ids) {
    const zeile = document.querySelector<HTMLElement>(`[data-frist-zeile="${CSS.escape(id)}"]`);
    // die Fläche der Zeile (Reiter, Startseite, Akte; im Brief `.dok-frist-flaeche`), sonst die Zeile selbst (tabIndex -1)
    if (zeile) return zeile.querySelector<HTMLElement>(".fz-flaeche:is(a, button), .dok-frist-flaeche") ?? zeile;
  }
  return null;
}

/** Tipps mit dem Zeiger auf „Löschen“ zählen erst nach dieser Zeit ab dem Öffnen (Doppeltipp auf „Frist löschen“ oder
 *  „Löschen“ im Detail trifft sonst die Antwort, Memory `doppeltipp-dialog-probe`); die Tastatur gilt sofort. */
const SPERRE_MS = 600;

/** W82 Web-5h1 (Z05 bis Z09): Rückfrage „Frist löschen?“ (zentrierter Dialog aus `Rueckfrage.tsx`, kein Blatt). Gesperrt während
 *  des Löschens; nach 204 oder 404 (schon gelöscht) zu, Listen neu, „Gelöscht.“, Fokus auf die nachrückende Zeile oder auf
 *  `leerZiel` (etwa „Neu“), nie `body`. Scheitert das Löschen (Zeitgrenze, kein Netz), schließt sie und `onFehler` meldet. */
export function FristLoeschen(props: {
  frist: Frist | undefined;
  offen: boolean;
  onSchliessen: () => void;
  /** nach 204 oder 404; `nachbarn` sind die nachrückenden Zeilen (danach, davor) */
  onGeloescht: (nachbarn: string[]) => void;
  onFehler: (fehler: unknown) => void;
  /** Sicherung des Blatts (gesperrt wie das Blatt); ohne Blatt (Detail) eine eigene */
  sicherung?: ReturnType<typeof useSichern>;
  leerZiel?: (() => HTMLElement | null) | undefined;
  /** meldet das Ziel des Fokus nach dem Löschen an das Blatt (es schließt nach der Rückfrage) */
  zielSetzen?: (ziel: () => HTMLElement | null) => void;
}) {
  const loeschen = useFristLoeschen();
  const toast = useToast();
  const eigene = useSichern(props.offen);
  const sicherung = props.sicherung ?? eigene;
  const gesendet = useRef(false);
  const ziel = useRef<() => HTMLElement | null>(() => null);
  const [bereit, setBereit] = useState(false);
  const [warOffen, setWarOffen] = useState(props.offen);
  if (warOffen !== props.offen) {
    setWarOffen(props.offen);
    setBereit(false);
  }
  useEffect(() => {
    if (!props.offen) return;
    ziel.current = () => null;
    const uhr = setTimeout(() => setBereit(true), SPERRE_MS);
    return () => clearTimeout(uhr);
  }, [props.offen]);
  const f = props.frist;
  // Z05 nur für eine Frist aus dem Brief (`herkunft` brief): nur sie merkt sich der Server (5h-S2), eine geänderte oder von Hand
  // angelegte nicht (Z06 „ohne Brief oder von Hand“)
  const ausBrief = Boolean(f?.dokument_id) && f?.herkunft !== "hand";
  function handlung() {
    if (!f || gesendet.current || sicherung.laeuft) return;
    gesendet.current = true;
    const nachbarn = nachrueckend(f.id);
    const nachher = () => zeileZiel(nachbarn) ?? props.leerZiel?.() ?? document.querySelector<HTMLElement>("main h1");
    ziel.current = nachher;
    props.zielSetzen?.(nachher);
    sicherung.sichern((signal) =>
      loeschen.mutateAsync(
        { id: f.id, signal },
        {
          onSuccess: () => {
            toast(FRIST_TEXTE.geloescht);
            props.onGeloescht(nachbarn);
          },
          onError: (e) => {
            ziel.current = () => null;
            props.onSchliessen();
            props.onFehler(e);
          },
          onSettled: () => {
            gesendet.current = false;
          },
        },
      ),
    );
  }
  return (
    <Rueckfrage
      offen={props.offen}
      kennung="frist.loeschen"
      titel={FRIST_TEXTE.loeschenTitel}
      text={ausBrief ? FRIST_TEXTE.loeschenBrief : FRIST_TEXTE.loeschen}
      behalten={FRIST_TEXTE.behalten}
      // W82 Web-5h-N2b-2 (Punkt 5, HW31, D-733): die sichere Wahl trägt den Akzent der Welt, die Handlung bleibt die ruhige Glas-Pille
      behaltenArt="primaer"
      handlung={FRIST_TEXTE.loeschenKnopf}
      gesperrt={sicherung.laeuft}
      zeigerBereit={bereit}
      fokusNachher={() => ziel.current()}
      onSchliessen={props.onSchliessen}
      onHandlung={handlung}
    />
  );
}

/** W82 Web-5c (K15, entscheide E6, F5 = A): Blatt „Neue Frist“ aus dem Reiter Fristen. Titel (Pflicht ohne Akte, höchstens
 *  120 Zeichen), Art (Vorgabe Zahlung), Datum (Pflicht, Grenzen wie der Server), Betrag (frei), Akte (frei). „Sichern“ ruft
 *  `POST /api/fristen` (Server 5a) und sendet einmal; bis zur Antwort schließt nichts das Blatt (D-089, `sichern.ts`). Danach
 *  „Frist angelegt.“, das Blatt schließt und `onAngelegt` bekommt die neue Frist (Fokus auf ihre Zeile über `fokusNachher`).
 *  Scheitert das Sichern, nachdem das Blatt weg ist, meldet das Band über der Liste.
 *  W82 Web-5h1 (K45, D-621 Fragen 45 = B und 46 = C, F1 = A, F2 = A): mit `frist` das Blatt „Frist“ (Modus „bestehend“, Z10) für jede
 *  Frist: ohne Brief Titel, Art, Datum, Betrag, Akte und „Für wen“ (ab zwei Personen, Vorgabe „Gemeinsam“); mit Brief Art, Datum,
 *  Betrag, darunter leise „Aus Brief: …“ und „Für wen: Ben, wie der Brief“ (Z13). „Sichern“ erst nach einer Änderung, `PATCH` nur
 *  mit den geänderten Feldern, danach „Frist geändert.“ (Z11) und der Fokus zurück auf die Zeile. Unten „Brief öffnen“ (nicht aus
 *  dem Brief) und „Frist löschen“ (Rückfrage, `FristLoeschen`). Die Frist kommt, wenn da, aus `GET /api/fristen?offen=false`
 *  (die Akte liefert nur `FristBasis` ohne Titel und Person). Modus „neu“ bekommt „Für wen“ und sendet `person_id` nur bei einer
 *  Person. Ein Blatt (`Sheet`) für beide Modi (Inventar 49). */
export function FristNeuBlatt(props: {
  offen: boolean;
  onSchliessen: () => void;
  onAngelegt?: (frist: Frist) => void;
  fokusNachher?: () => HTMLElement | null;
  frist?: Frist | undefined;
  /** Brief der Frist, wenn der Aufrufer ihn kennt („Aus Brief: …“), und seine Herkunftszeile (Absender) */
  brief?: Pick<Dokument, "id" | "dokumentart" | "betreff" | "datum" | "erstellt"> | undefined;
  woher?: string | undefined;
  /** aus dem Brief geöffnet: ohne „Brief öffnen“ */
  ausBrief?: boolean;
  /** W82 Web-8 W8-3 (W88 NW-6): Vorschlag des Briefs; ist die Frist dort abgeleitet, zeigt das Blatt den Hinweis des Servers mit
   *  Beleg. Nur die Dokumentansicht gibt ihn (Fristen-Seite und Akte bleiben ohne Kennzeichen, Server-Vormerkung V-W88-1) */
  vorschlag?: Pick<Vorschlag, "fristen"> | null | undefined;
  /** Fokus nach dem Löschen, wenn keine Zeile nachrückt („Neu“ im Reiter Fristen) */
  leerZiel?: () => HTMLElement | null;
  /** R1 W82-Web-5h1: nach dem Löschen (204 oder 404) mit den nachrückenden Zeilen (danach, davor); ab 1280 px wählt der Reiter
   *  Fristen damit die nächste im Detail, wie „Löschen“ im Detail */
  onGeloescht?: (nachbarn: string[]) => void;
}) {
  const bestehend = props.frist !== undefined;
  const { data: alleFristen } = useFristen(false, bestehend && props.offen);
  const frist = props.frist ? (alleFristen?.find((f) => f.id === props.frist?.id) ?? props.frist) : undefined;
  const mitBrief = Boolean(frist?.dokument_id);
  const [aenderung, setAenderung] = useState<Partial<Werte>>({});
  const [geprueft, setGeprueft] = useState(false);
  const [fehler, setFehler] = useState<Fehler>({});
  const [frage, setFrage] = useState(false);
  const { data: akten } = useAkten();
  const { data: personen } = usePersonen();
  // Regel der Personen-Marken (Plan 6.6): „Für wen“ erst ab zwei Personen im Haushalt, sonst wird nichts gesendet
  const mehrere = (personen?.length ?? 0) >= 2;
  const anlegen = useFristAnlegen();
  const aendern = useFristAendern();
  const toast = useToast();
  const navigieren = useNavigate();
  const sicherung = useSichern(props.offen);
  // ein Tipp, eine Anfrage: auch ein zweiter Klick vor dem nächsten Bild sendet nichts mehr
  const gesendet = useRef(false);
  // nach dem Löschen: Fokus auf die nachrückende Zeile statt auf die gelöschte (FristLoeschen meldet das Ziel)
  const loeschZiel = useRef<(() => HTMLElement | null) | null>(null);
  const titelFeld = useRef<HTMLInputElement>(null);
  const datumFeld = useRef<HTMLInputElement>(null);
  const betragFeld = useRef<HTMLInputElement>(null);

  const w: Werte = { ...(frist ? werteVon(frist) : LEER), ...aenderung };
  const setzen = (teil: Partial<Werte>) => setAenderung((a) => ({ ...a, ...teil }));
  const koerper = frist ? fristAenderung(frist, w, mehrere) : null;
  const geaendert = koerper !== null && Object.keys(koerper).length > 0;
  const grenzen = fristGrenzen(heuteBerlin());
  const datumSatz = FRIST_NEU_TEXTE.datum(datumText(grenzen.von), datumText(grenzen.bis));
  const betragWert = betragEingabe(w.betrag);
  const betragFalsch = w.betrag.trim() !== "" && (betragWert === null || Number(betragWert) >= 1e10);
  const datumFalsch = w.tag !== "" && (w.tag < grenzen.von || w.tag > grenzen.bis);
  const titelFehlt = !mitBrief && !w.titel.trim() && !w.akteId;
  const fehlerTitel = fehler.titel ?? (geprueft && titelFehlt ? FRIST_NEU_TEXTE.ohneBezug : undefined);
  const fehlerDatum = fehler.datum ?? (datumFalsch || (geprueft && !w.tag) ? datumSatz : undefined);
  const fehlerBetrag = fehler.betrag ?? (betragFalsch ? FRIST_NEU_TEXTE.betrag : undefined);
  // R1 (Befund A): jedes Öffnen beginnt leer bzw. mit den Werten der Frist. Das Blatt bleibt eingehängt; nach „Abbrechen“
  // standen sonst Titel, Betrag, Akte und schon rote Feldfehler im nächsten Blatt (gesetzt beim Rendern, damit kein altes Bild
  // aufblitzt)
  const [warOffen, setWarOffen] = useState(props.offen);
  if (warOffen !== props.offen) {
    setWarOffen(props.offen);
    if (props.offen) zuruecksetzen();
  }

  function zuruecksetzen() {
    setAenderung({});
    setGeprueft(false);
    setFehler({});
    setFrage(false);
  }
  useEffect(() => {
    if (props.offen) loeschZiel.current = null;
  }, [props.offen]);

  function sichern() {
    if (gesendet.current || sicherung.laeuft) return;
    if (frist && !geaendert) return;
    setGeprueft(true);
    setFehler({});
    // das erste falsche Feld bekommt den Fokus, sein Fehler ist seine Beschreibung
    if (titelFehlt) return titelFeld.current?.focus();
    if (!w.tag || datumFalsch) return datumFeld.current?.focus();
    if (betragFalsch) return betragFeld.current?.focus();
    gesendet.current = true;
    const rueckrufe = {
      onError: (e: unknown) => setFehler(serverFehler(e, datumSatz)),
      onSettled: () => {
        gesendet.current = false;
      },
    };
    // BS30: nach SICHERN_GRENZE_MS ohne Antwort bricht `useSichern` ab (onError mit dem Netz-Satz, Knöpfe wieder frei)
    if (frist && koerper) {
      sicherung.sichern((signal) =>
        aendern.mutateAsync(
          { ...koerper, id: frist.id, signal },
          {
            ...rueckrufe,
            onSuccess: () => {
              toast(FRIST_TEXTE.geaendert);
              props.onSchliessen();
            },
          },
        ),
      );
      return;
    }
    const person = mehrere && w.personId ? { person_id: w.personId } : {};
    sicherung.sichern((signal) =>
      anlegen.mutateAsync(
        { art: w.art, datum: w.tag, betrag: betragWert, akte_id: w.akteId || null, titel: w.titel.trim() || null, ...person, signal },
        {
          ...rueckrufe,
          onSuccess: (neu) => {
            props.onAngelegt?.(neu);
            toast(FRIST_NEU_TEXTE.angelegt);
            zuruecksetzen();
            props.onSchliessen();
          },
        },
      ),
    );
  }

  const abgeleitet = frist && mitBrief ? abgeleiteteFrist(props.vorschlag, frist) : undefined;
  const fuerBrief = frist && mitBrief && mehrere && (frist.gemeinsam !== undefined || frist.person_id !== undefined) ? fuerWenBrief(frist, personen) : null;
  return (
    <Sheet
      offen={props.offen}
      onSchliessen={props.onSchliessen}
      gesperrt={sicherung.laeuft}
      fokusNachher={() => loeschZiel.current?.() ?? props.fokusNachher?.() ?? null}
      titel={frist ? FRIST_TEXTE.titel : FRIST_NEU_TEXTE.titel}
      rechts={
        <button
          type="button"
          disabled={sicherung.laeuft || (frist !== undefined && !geaendert)}
          onClick={sichern}
          data-testid="frist-neu.sichern"
          className="t-headline min-h-(--touch) text-pg-accent-text disabled:text-pg-text-3"
        >
          Sichern
        </button>
      }
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          sichern();
        }}
      >
        <ul className="gruppe mt-4 overflow-clip rounded-group bg-pg-surface">
          {!mitBrief && (
            <Feld
              ref={titelFeld}
              label="Titel"
              value={w.titel}
              // Kein natives maxLength: das zählt UTF-16-Einheiten, der Server Zeichen (wie „Dokumentart“)
              onChange={(e) => {
                setzen({ titel: hoechstens(e.target.value, TITEL_MAX) });
                setFehler((f) => ({ ...f, titel: undefined }));
              }}
              autoFocus={!frist}
              aria-invalid={Boolean(fehlerTitel) || undefined}
              hinweis={fehlerTitel && <span className="text-pg-danger">{fehlerTitel}</span>}
            />
          )}
          <Auswahl label="Art" value={w.art} onChange={(e) => setzen({ art: e.target.value as Fristart })}>
            {Object.keys(FRISTARTEN).map((wert) => (
              <option key={wert} value={wert}>
                {FRISTWORT[wert] ?? FRISTARTEN[wert]}
              </option>
            ))}
          </Auswahl>
          <Feld
            ref={datumFeld}
            label="Datum"
            type="date"
            value={w.tag}
            min={grenzen.von}
            max={grenzen.bis}
            onChange={(e) => {
              setzen({ tag: e.target.value });
              setFehler((f) => ({ ...f, datum: undefined }));
            }}
            className="t-zahl"
            aria-invalid={Boolean(fehlerDatum) || undefined}
            hinweis={fehlerDatum && <span className="text-pg-danger">{fehlerDatum}</span>}
          />
          <Feld
            ref={betragFeld}
            label="Betrag in Euro"
            inputMode="decimal"
            value={w.betrag}
            onChange={(e) => {
              setzen({ betrag: e.target.value });
              setFehler((f) => ({ ...f, betrag: undefined }));
            }}
            className="t-zahl"
            aria-invalid={Boolean(fehlerBetrag) || undefined}
            hinweis={fehlerBetrag && <span className="text-pg-danger">{fehlerBetrag}</span>}
          />
          {!mitBrief && (
            <Auswahl
              // W82 Web-6a (WN-20): Schrift bei schmalem Fenster und großer Schrift wie das Tippfeld (bausteine.css, D-739)
              data-aktenwahl=""
              label="Akte"
              value={w.akteId}
              onChange={(e) => {
                setzen({ akteId: e.target.value });
                setFehler((f) => ({ ...f, titel: undefined }));
              }}
            >
              <option value="">Keine Akte</option>
              {(akten ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Auswahl>
          )}
          {!mitBrief && mehrere && (
            // Z03: Personen mit vollem Namen wie das Prüfen-Blatt, dann „Gemeinsam“ (Vorgabe nach F1 = A)
            <Auswahl label={FRIST_TEXTE.fuerWen} value={w.personId} onChange={(e) => setzen({ personId: e.target.value })}>
              {(personen ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value="">{FRIST_TEXTE.gemeinsam}</option>
            </Auswahl>
          )}
        </ul>
        {/* Enter in einem Feld sichert wie der Knopf oben */}
        <button type="submit" hidden tabIndex={-1} aria-hidden />
      </form>
      {frist && mitBrief && (props.brief || fuerBrief) && (
        <div className="frist-blatt-brief t-footnote mt-3 flex flex-col gap-1 px-4 text-pg-text-2" data-testid="frist-blatt-brief">
          {props.brief && (
            <p>
              {FRIST_TEXTE.ausBrief(`${props.brief.dokumentart ?? props.brief.betreff ?? "Brief"} vom ${datumText(props.brief.datum ?? props.brief.erstellt)}${props.woher ? ` · ${props.woher}` : ""}`)}
            </p>
          )}
          {fuerBrief && <p>{fuerBrief}</p>}
        </div>
      )}
      {abgeleitet?.abgeleitet && (
        <div className="t-footnote mt-3 flex flex-col gap-1 px-4 break-words hyphens-auto text-pg-text-2" data-testid="frist-blatt-abgeleitet">
          <p>
            {abgeleitet.abgeleitet.hinweis} · {abgeleitetBasis(abgeleitet.abgeleitet)}
          </p>
          {abgeleitet.beleg && <p className="italic">„{abgeleitet.beleg}“</p>}
        </div>
      )}
      {fehler.gesamt !== undefined && <Fehlertext fehler={fehler.gesamt} />}
      {frist && (
        <div className="frist-blatt-knoepfe mt-6 flex flex-wrap gap-3 px-4">
          {mitBrief && !props.ausBrief && (
            <Knopf
              art="sekundaer"
              disabled={sicherung.laeuft}
              onClick={() => {
                props.onSchliessen();
                void navigieren(`/dokument/${frist.dokument_id}`);
              }}
            >
              {FRIST_TEXTE.briefOeffnen}
            </Knopf>
          )}
          <Knopf art="rot" aria-haspopup="dialog" disabled={sicherung.laeuft} onClick={() => setFrage(true)}>
            {FRIST_TEXTE.loeschenUnten}
          </Knopf>
        </div>
      )}
      <FristLoeschen
        frist={frist}
        offen={frage}
        sicherung={sicherung}
        leerZiel={props.leerZiel}
        zielSetzen={(z) => {
          loeschZiel.current = z;
        }}
        onSchliessen={() => setFrage(false)}
        onGeloescht={(nachbarn) => {
          setFrage(false);
          props.onGeloescht?.(nachbarn);
          props.onSchliessen();
        }}
        onFehler={(e) => {
          loeschZiel.current = null;
          setFehler({ gesamt: e instanceof ApiFehler ? e : new Error(fehlerText(e)) });
        }}
      />
    </Sheet>
  );
}

/** Z13: „Für wen: Ben, wie der Brief“ (Vorname), gemeinsam „Gemeinsam“, ohne Person „Niemand bestimmtes“. */
function fuerWenBrief(f: Frist, personen: { id: string; name: string }[] | undefined): string {
  if (f.gemeinsam) return FRIST_TEXTE.fuerWenBrief(FRIST_TEXTE.gemeinsam);
  const p = f.person_id ? personen?.find((x) => x.id === f.person_id) : undefined;
  return FRIST_TEXTE.fuerWenBrief(p ? (p.name.split(" ")[0] ?? p.name) : FRIST_TEXTE.niemand);
}

